import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CtaBand } from "@/components/Section";

const EFFECTIVE = "Effective date: August 1, 2026";

const CONTENT = {
  privacy: {
    eyebrow: "Legal",
    title: "Privacy Policy",
    desc: "Privacy Policy for Precision Maintenance1 LLC (PM1 Clean).",
    intro:
      "Precision Maintenance1 LLC (\u201cPM1 Clean,\u201d \u201cwe,\u201d \u201cus,\u201d or \u201cour\u201d) respects your privacy. This Privacy Policy explains what information we collect through our website and services, how we use it, and the choices available to you.",
    sections: [
      {
        h: "Information We Collect",
        p: [
          "We collect information you voluntarily provide when you request a quote, schedule a facility walkthrough, or contact us. This may include your name, company, job title, phone number, email address, facility address and details, and any information you include in your message.",
          "We may also automatically collect limited technical information such as your browser type, device, and general usage data through standard web technologies to help us operate and improve the site.",
        ],
      },
      {
        h: "How We Use Your Information",
        p: [
          "We use the information you provide to respond to your inquiries, prepare customized cleaning proposals, schedule walkthroughs, provide and manage our services, and communicate with you about your account or requests.",
          "We do not sell your personal information.",
        ],
      },
      {
        h: "How We Share Information",
        p: [
          "We share information only as needed to operate our business \u2014 for example, with trusted service providers who help us deliver services (such as email delivery), or when required by law, regulation, or legal process. Any service providers are expected to protect your information and use it only for the purposes we specify.",
        ],
      },
      {
        h: "Data Retention",
        p: [
          "We retain form submissions and related records for as long as necessary to fulfill the purposes described in this policy, to maintain our business relationship, and to comply with our legal obligations.",
        ],
      },
      {
        h: "Security",
        p: [
          "We use reasonable administrative, technical, and physical safeguards designed to protect the information we collect. However, no method of transmission or storage is completely secure, and we cannot guarantee absolute security.",
        ],
      },
      {
        h: "Your Choices and Rights",
        p: [
          "You may request access to, correction of, or deletion of the personal information you have provided to us by contacting us using the details below. Depending on your location, you may have additional rights under applicable law.",
        ],
      },
      {
        h: "Cookies and Analytics",
        p: [
          "Our website may use cookies or similar technologies to support core functionality and understand how the site is used. You can control cookies through your browser settings; disabling them may affect some site features.",
        ],
      },
      {
        h: "Children\u2019s Privacy",
        p: [
          "Our website and services are intended for businesses and are not directed to children under 13. We do not knowingly collect personal information from children.",
        ],
      },
      {
        h: "Changes to This Policy",
        p: [
          "We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date.",
        ],
      },
      {
        h: "Contact Us",
        p: [
          "If you have questions about this Privacy Policy or our data practices, contact us at info@pm1clean.com or +1 800 373 2708, or write to Precision Maintenance1 LLC, 90 E Halsey Rd, Suite 332, Parsippany, NJ 07054.",
        ],
      },
    ],
  },
  terms: {
    eyebrow: "Legal",
    title: "Terms of Service",
    desc: "Terms of Service for Precision Maintenance1 LLC (PM1 Clean).",
    intro:
      "These Terms of Service (\u201cTerms\u201d) govern your use of the website operated by Precision Maintenance1 LLC (\u201cPM1 Clean\u201d). By accessing or using this website, you agree to these Terms. If you do not agree, please do not use the site.",
    sections: [
      {
        h: "Use of the Website",
        p: [
          "You may use this website for lawful purposes only. You agree not to misuse the site, interfere with its operation, attempt to gain unauthorized access, or use it in any way that could harm PM1 Clean or others.",
        ],
      },
      {
        h: "Quotes, Estimates, and Proposals",
        p: [
          "Information on this website, including any quote or estimate you request, is provided for general informational purposes and does not constitute a binding offer or contract. Pricing and scope are confirmed only through a written service agreement signed by PM1 Clean and the client.",
        ],
      },
      {
        h: "Service Agreements",
        p: [
          "Any commercial cleaning or facility-maintenance services we provide are governed by the specific written agreement entered into between PM1 Clean and the client. In the event of a conflict, that service agreement controls over these Terms.",
        ],
      },
      {
        h: "Submissions",
        p: [
          "Any information you submit through our forms must be accurate and your own to share. You grant us permission to use the information you provide to respond to and fulfill your request.",
        ],
      },
      {
        h: "Intellectual Property",
        p: [
          "The content, branding, logos, text, and design on this website are the property of Precision Maintenance1 LLC or its licensors and are protected by applicable laws. You may not copy, reproduce, or reuse them without our prior written permission.",
        ],
      },
      {
        h: "Disclaimers",
        p: [
          "The website is provided \u201cas is\u201d and \u201cas available\u201d without warranties of any kind, whether express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the site will be uninterrupted or error-free.",
        ],
      },
      {
        h: "Limitation of Liability",
        p: [
          "To the fullest extent permitted by law, PM1 Clean will not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of the website.",
        ],
      },
      {
        h: "Third-Party Links",
        p: [
          "Our website may contain links to third-party sites. We are not responsible for the content or practices of those sites and encourage you to review their policies.",
        ],
      },
      {
        h: "Governing Law",
        p: [
          "These Terms are governed by the laws of the State of New Jersey, without regard to its conflict-of-laws principles.",
        ],
      },
      {
        h: "Changes to These Terms",
        p: [
          "We may update these Terms from time to time. Continued use of the website after changes are posted constitutes acceptance of the revised Terms.",
        ],
      },
      {
        h: "Contact Us",
        p: [
          "Questions about these Terms may be directed to info@pm1clean.com or +1 800 373 2708, Precision Maintenance1 LLC, 90 E Halsey Rd, Suite 332, Parsippany, NJ 07054.",
        ],
      },
    ],
  },
  accessibility: {
    eyebrow: "Legal",
    title: "Accessibility Statement",
    desc: "Accessibility Statement for Precision Maintenance1 LLC (PM1 Clean).",
    intro:
      "Precision Maintenance1 LLC (\u201cPM1 Clean\u201d) is committed to ensuring that our website is accessible to people of all abilities and to providing an inclusive experience for every visitor.",
    sections: [
      {
        h: "Our Commitment",
        p: [
          "We strive to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA. These guidelines help make web content more accessible to people with a wide range of disabilities.",
        ],
      },
      {
        h: "Measures We Take",
        p: [
          "Our website is built with semantic HTML, keyboard-navigable controls, visible focus states, descriptive alternative text for meaningful images, clear heading structure, and color choices intended to meet contrast recommendations.",
        ],
      },
      {
        h: "Compatibility",
        p: [
          "The website is designed to be compatible with current versions of major browsers and to work with assistive technologies such as screen readers where reasonably possible.",
        ],
      },
      {
        h: "Ongoing Effort",
        p: [
          "Accessibility is an ongoing effort. We continue to review and improve the website, and we welcome feedback that helps us identify and address barriers.",
        ],
      },
      {
        h: "Feedback and Contact",
        p: [
          "If you encounter an accessibility barrier on our website, or if you need information in an alternative format, please contact us at info@pm1clean.com or +1 800 373 2708. We will make reasonable efforts to provide the information you need and to address the issue.",
        ],
      },
    ],
  },
};

export default function Legal({ type }) {
  const c = CONTENT[type] || CONTENT.privacy;
  return (
    <>
      <Seo title={c.title} description={c.desc} path={`/${type}`} />
      <PageHero eyebrow={c.eyebrow} title={c.title} />
      <article className="container-px mx-auto max-w-[860px] py-20 lg:py-24">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.18em] text-steel">{EFFECTIVE}</p>
        </Reveal>
        <Reveal delay={0.05}>
          <p className="mt-6 text-lg leading-relaxed text-steel">{c.intro}</p>
        </Reveal>

        <div className="mt-12 flex flex-col gap-10">
          {c.sections.map((s, i) => (
            <Reveal key={i} delay={0.03 * i}>
              <section data-testid={`legal-section-${i}`}>
                <h2 className="font-display text-xl font-bold tracking-tight text-navy sm:text-2xl">{s.h}</h2>
                <div className="mt-4 flex flex-col gap-4">
                  {s.p.map((para, j) => (
                    <p key={j} className="text-base leading-relaxed text-steel">{para}</p>
                  ))}
                </div>
              </section>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <p className="mt-14 border-t border-border pt-6 text-xs leading-relaxed text-steel/70">
            This document is provided for general informational purposes and does not constitute legal advice. We
            recommend review by qualified counsel before publication.
          </p>
        </Reveal>
      </article>

      <CtaBand
        title="Request a Customized Cleaning Plan"
        body=""
        primary={{ label: "Request a Customized Cleaning Plan", to: "/request-a-quote" }}
        secondary={{ label: "Talk With Our Team", to: "/contact" }}
      />
    </>
  );
}
